import type { AlgoliaHit } from '../../types';
import { useMemo } from 'react';
import moment from 'moment';
import { Stack, Row, Col, Divider, Link } from '@workramp/ui';
import StringUtils from '~/common/string-utils';
import { sanitizeUserInput } from '~/utils/sanitize-user-input';
import { getAcademiesViewUrl } from '~/clc/features/academies/utils';
import { I18n } from '~/common/explicit-exports';
import { getCommunitiesViewUrl } from '~/clc/features/community/learner/utils';
import { StyledHighlightSpan } from '../HubLearnerSearchBar/HubLearnerSearchBar.styles';
import { formatAlgoliaHitForHubResult } from '../../utils';
import { StyledTitle, StyledSearchResultFooterInfo } from './SearchResult.styles';

const { APP_CONSTANTS } = window;

const PARENT_TYPES_WITH_SUBRESOURCES = [
  APP_CONSTANTS.ASSET_PICKER_TYPES.ACADEMY_CERTIFICATION,
  APP_CONSTANTS.ASSET_PICKER_TYPES.ACADEMY_PATH,
  APP_CONSTANTS.ASSET_PICKER_TYPES.ACADEMY_COLLECTION,
  APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_CUSTOM_PAGE,
];
const COMMUNITY_TYPES = [
  APP_CONSTANTS.NON_ASSET_PICKER_TYPES.COMMUNITY_FORUM_TOPIC,
  APP_CONSTANTS.NON_ASSET_PICKER_TYPES.COMMUNITY_FORUM_POST,
  APP_CONSTANTS.NON_ASSET_PICKER_TYPES.COMMUNITY_FORUM_COMMENT,
];

const TRANSLATED_UPDATED = I18n.t('js.learner.advanced_search.filters.updated');
const MAX_TITLE_LENGTH = 150;
const MAX_BODY_LENGTH = 250;

export function SearchResult({
  hit,
  academyContentTypesToLocalizedString,
}: {
  hit: AlgoliaHit;
  academyContentTypesToLocalizedString: Record<string, string>;
}) {
  const result = formatAlgoliaHitForHubResult(hit);
  const { resultTitle, resultBody, parentType, parentName, type, contentType, highlightedResult } = result as any;
  const resultHref = (COMMUNITY_TYPES as string[]).includes(type)
    ? getCommunitiesViewUrl(result)
    : getAcademiesViewUrl(result);

  const contentTypeString = useMemo(() => {
    if (PARENT_TYPES_WITH_SUBRESOURCES.includes(parentType)) {
      const contentTypeFormatted =
        type === 'academy_custom_page_object' && ['collection', 'certification'].includes(contentType)
          ? `academy_${contentType}`
          : contentType;
      return `${academyContentTypesToLocalizedString[contentTypeFormatted]} • ${parentName}`.toUpperCase();
    }

    // session and ODC aren't indexed with contentType :(
    if (parentType === APP_CONSTANTS.ASSET_PICKER_TYPES.ACADEMY_EVENT) {
      return `${academyContentTypesToLocalizedString[type]} • ${parentName}`.toUpperCase();
    }
    return (
      academyContentTypesToLocalizedString[type] ||
      StringUtils.getCapitalizedWordsFromUnderscoredString(type).toUpperCase()
    ).toUpperCase();
  }, [type, contentType, parentName, parentType, academyContentTypesToLocalizedString]);

  const rawResultTitle = highlightedResult?.name?.value || resultTitle;
  const formattedResultTitle =
    rawResultTitle.length > MAX_TITLE_LENGTH ? `${rawResultTitle.slice(0, MAX_TITLE_LENGTH)}...` : rawResultTitle;
  const rawResultBody = highlightedResult?.description?.value || resultBody;
  const formattedResultBody =
    rawResultBody?.length > MAX_BODY_LENGTH ? `${rawResultBody.slice(0, MAX_BODY_LENGTH)}...` : rawResultBody;

  return (
    <Stack size={4}>
      <Row>
        <Col xs="auto">
          <Link href={resultHref}>
            <StyledTitle
              size={1}
              weight="medium"
              dangerouslySetInnerHTML={{
                __html: sanitizeUserInput(formattedResultTitle, {
                  allowSafeTags: true,
                }),
              }}
            />
          </Link>
        </Col>
      </Row>
      {resultBody && (
        <Row>
          <StyledHighlightSpan
            size={0}
            dangerouslySetInnerHTML={{
              __html: sanitizeUserInput(formattedResultBody, {
                allowSafeTags: true,
              }),
            }}
          />
        </Row>
      )}
      <Row>
        <Col>
          <StyledSearchResultFooterInfo size={0} weight="medium">
            {contentTypeString}{' '}
          </StyledSearchResultFooterInfo>
          <StyledSearchResultFooterInfo size={0}>
            • {TRANSLATED_UPDATED} {moment(hit.updated_at).format('MMMM D, YYYY')}
          </StyledSearchResultFooterInfo>
        </Col>
      </Row>
      <Divider />
    </Stack>
  );
}
